import React, { useState } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import API from '../services/api';

interface ImportDetail {
  row: number;
  status: 'imported' | 'skipped' | 'error';
  caseNumber?: string;
  country?: string;
  victimCount?: number;
  reason?: string;
  error?: string;
}

interface ImportSummary {
  message: string;
  imported: number;
  skipped: number;
  errors: number;
  details: ImportDetail[];
}

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImportComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setError('');
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File exceeds the 10 MB limit');
      return;
    }

    setUploading(true);
    setError('');
    setSummary(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post('/api/cases/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSummary(response.data);
      if (response.data.imported > 0) {
        onImportComplete();
      }
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.message || data?.error || 'Failed to import CSV');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setFile(null);
    setSummary(null);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center">
      <div className="relative top-16 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Import Cases from CSV</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Upload a JotForm-format CSV to bulk-create cases. Each row will be created as a
          FOR VALIDATION case attributed to you. Rows with an already-imported Submission ID are skipped.
          Max file size: 10 MB.
        </p>

        {!summary && (
          <div className="mb-4">
            <label className="block">
              <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="text-center">
                  <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {file ? (
                      <span className="font-medium text-gray-900 dark:text-gray-100">{file.name}</span>
                    ) : (
                      <>Click to select a CSV file</>
                    )}
                  </p>
                  {file && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                </div>
              </div>
              <input
                type="file"
                accept=".csv,text/csv,application/csv,application/vnd.ms-excel"
                className="hidden"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </label>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        {summary && (
          <div className="mb-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg mb-3">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{summary.message}</p>
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="p-2 bg-green-100 rounded">
                  <div className="text-xl font-bold text-green-800">{summary.imported}</div>
                  <div className="text-xs text-green-700">Imported</div>
                </div>
                <div className="p-2 bg-yellow-100 rounded">
                  <div className="text-xl font-bold text-yellow-800">{summary.skipped}</div>
                  <div className="text-xs text-yellow-700">Skipped</div>
                </div>
                <div className="p-2 bg-red-100 rounded">
                  <div className="text-xl font-bold text-red-800">{summary.errors}</div>
                  <div className="text-xs text-red-700">Errors</div>
                </div>
              </div>
            </div>

            {summary.details.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {summary.details.map((d, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{d.row}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded font-medium ${
                              d.status === 'imported'
                                ? 'bg-green-100 text-green-800'
                                : d.status === 'skipped'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {d.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {d.status === 'imported' && (
                            <>
                              {d.caseNumber} {d.country && `• ${d.country}`}
                              {typeof d.victimCount === 'number' && ` • ${d.victimCount} victim${d.victimCount === 1 ? '' : 's'}`}
                            </>
                          )}
                          {d.status === 'skipped' && d.reason}
                          {d.status === 'error' && d.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {summary ? 'Close' : 'Cancel'}
          </button>
          {!summary && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                  Import CSV
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
